import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import {AgentStatusType} from "../../../types/agentStatus.type";
import {ClockInResourceProviderType} from "../../../../timekeeping/angular/models/planned-resource.model";
import * as _ from "lodash";
import {BreakType} from "../../../../site-preferences/models/break-type.model";
import {EnforceMinimumBreakEnum} from "../../../../site-preferences/types/enforce-minimum-break.enum";

require('./agent-status.scss');
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-agent-status',
  templateUrl: './agent-status.html',
  encapsulation: ViewEncapsulation.None
})
export class AgentStatusComponent implements OnInit, OnChanges {

  @Input() status: AgentStatusType;
  @Input() agentId: string;
  @Input() agentName: string;
  @Input() statusUpdatedAt: number;
  @Input() activeBreakTypeId: string;
  @Input() isLarge: boolean;
  @Input() isOutline: boolean;

  @Output() onChangeStatus = new EventEmitter<null>();

  public isDropdownOpen: boolean = false;
  public isLoading: boolean = false;
  public agentStatusTypes: typeof AgentStatusType = AgentStatusType;

  // Status labels for display
  public statusLabels = {
    [AgentStatusType.OnDuty]: 'On Duty',
    [AgentStatusType.OnDutyOnBreak]: 'On Break',
    [AgentStatusType.OffDuty]: 'Off Duty'
  };

  // Timekeeping properties
  public isTimekeepingEnabled: boolean = false;
  public loadingProfile: boolean = false;
  public loadingResources: boolean = false;
  public agentConfigurationProfile: any;
  public clockInResources: any[] = [];

  // Active break (timekeeping only)
  public activeBreak: {breakType: BreakType, startTime: number, endTime: number, remainingMinutes: number};
  public isBreakChangeBlocked: boolean = false;
  public isBreakChangeWarning: boolean = false;

  constructor(
    @Inject('agentsService') private agentsService,
    @Inject('splitService') private splitService,
    @Inject('agentConfigurationProfilesRequestsService') private agentConfigurationProfilesRequestsService,
    @Inject('attendanceRequestsService') private attendanceRequestsService,
    @Inject('agentDialogService') private agentDialogService,
    @Inject('agentsAlerts') private agentsAlerts,
    @Inject('confirmDialogService') private confirmDialogService,
    @Inject('analyticsService') private analyticsService,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.isTimekeepingEnabled = this.splitService.isTimekeepingEnabled();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reset cached data when agent changes
    if (changes.agentId && !changes.agentId.isFirstChange()) {
      this.agentConfigurationProfile = null;
      this.clockInResources = [];
    }
  }

  // Called when main menu opens (clicking status button)
  onMainMenuOpen(isOpen?: boolean) {
    this.isDropdownOpen = !!isOpen;

    if (isOpen === false) {
      return;
    }

    this.refreshActiveBreak();

    if (!this.isTimekeepingEnabled || !this.agentId || this.agentConfigurationProfile || this.loadingProfile) {
      return;
    }

    this.loadingProfile = true;

    this.agentConfigurationProfilesRequestsService.getConfigurationByAgent(this.agentId)
      .then((profile) => {
        this.agentConfigurationProfile = profile;
        this.cdr.detectChanges();
      })
      .catch((err) => {
        console.error('Failed to load agent configuration profile:', err);
      })
      .finally(() => {
        this.loadingProfile = false;
        this.cdr.detectChanges();
      });
  }

  // Called when hovering over "On Duty" option
  onOnDutyMenuOpen() {
    if (!this.agentConfigurationProfile) {
      return;
    }

    const provider = _.get(this.agentConfigurationProfile, 'shiftRestrictions.agentClockInProviderPreferences.provider');
    // Don't fetch resources if provider is Default
    if (provider === ClockInResourceProviderType.Default) {
      return;
    }

    if (this.loadingResources || this.clockInResources.length > 0) {
      return;
    }

    this.fetchClockInResources();
  }

  isDefaultProvider(): boolean {
    const provider = _.get(this.agentConfigurationProfile, 'shiftRestrictions.agentClockInProviderPreferences.provider');
    return provider === ClockInResourceProviderType.Default;
  }

  isEnforceResourceBasedLogin(): boolean {
    return _.get(this.agentConfigurationProfile, 'shiftRestrictions.agentClockInProviderPreferences.enforceResourceBasedLogin', false);
  }

  canClockInWithoutResource(): boolean {
    // Can clock in without resource if:
    // 1. Provider is Default, OR
    // 2. Provider is Shift/Route AND enforceResourceBasedLogin is false
    const provider = _.get(this.agentConfigurationProfile, 'shiftRestrictions.agentClockInProviderPreferences.provider');
    const enforceResourceBasedLogin = this.isEnforceResourceBasedLogin();

    return provider === ClockInResourceProviderType.Default || !enforceResourceBasedLogin;
  }

  fetchClockInResources() {
    if (!this.agentConfigurationProfile || !this.agentId) {
      return;
    }

    const provider = _.get(this.agentConfigurationProfile, 'shiftRestrictions.agentClockInProviderPreferences.provider');
    if (provider === ClockInResourceProviderType.Shift || provider === ClockInResourceProviderType.Route) {
      this.loadingResources = true;

      this.attendanceRequestsService.getClockInResources({
        agentId: this.agentId,
        provider: provider,
        limit: 20
      })
        .then((response: any) => {
          // Handle both array response and object with list property
          const list = Array.isArray(response) ? response : (response?.list || []);
          if (list.length > 0) {
            this.clockInResources = list.map(resource => ({
              ...resource,
              provider: provider
            }));
          } else {
            this.clockInResources = [];
          }
          this.cdr.detectChanges();
        })
        .catch((error) => {
          console.error('Failed to load clock-in resources:', error);
          this.clockInResources = [];
          this.cdr.detectChanges();
        })
        .finally(() => {
          this.loadingResources = false;
          this.cdr.detectChanges();
        });
    }
  }

  onAdvanceClick(event: Event) {
    event.stopPropagation();
    this.confirmLeaveBreak(() => {
      this.agentDialogService.advanceOnDutyOption(event, this.agentId, this.agentConfigurationProfile, this.agentName)
        .then(() => {
          this.onChangeStatus.emit();
        });
    });
  }

  changeStatus(status: AgentStatusType, resource?: any) {
    if (status === AgentStatusType.OnDutyOnBreak && this.isTimekeepingEnabled) {
      this.startBreak();
      return;
    }

    this.confirmLeaveBreak(() => this.updateStatus(status, resource));
  }

  private refreshActiveBreak() {
    this.activeBreak = null;
    this.isBreakChangeBlocked = false;
    this.isBreakChangeWarning = false;

    if (!this.isTimekeepingEnabled || this.status !== AgentStatusType.OnDutyOnBreak) {
      return;
    }

    const breakType: BreakType = _.find(this.splitService.getBreakTypes(), {id: this.activeBreakTypeId});
    if (!breakType) {
      return;
    }

    const startTime = this.statusUpdatedAt;
    const endTime = startTime && breakType.durationMinutes ? startTime + breakType.durationMinutes * 60000 : null;
    const remainingMinutes = endTime ? Math.max(0, Math.ceil((endTime - Date.now()) / 60000)) : 0;

    this.activeBreak = {breakType, startTime, endTime, remainingMinutes};
    this.isBreakChangeBlocked = remainingMinutes > 0 && breakType.enforceMinimumBreakType === EnforceMinimumBreakEnum.HARD;
    this.isBreakChangeWarning = remainingMinutes > 0 && breakType.enforceMinimumBreakType === EnforceMinimumBreakEnum.SOFT;
  }

  // Admin attendance APIs bypass minimum break enforcement on the server, so it is applied here
  private confirmLeaveBreak(onConfirm: () => void) {
    this.refreshActiveBreak();

    if (this.isBreakChangeBlocked) {
      return;
    }

    if (!this.isBreakChangeWarning) {
      onConfirm();
      return;
    }

    this.confirmDialogService.confirmWithInfo('Break Time Not Ended', 'BREAK_TIME_NOT_ENDED_CONFIRM', null,
      {minutes: this.activeBreak.remainingMinutes})
      .then(() => onConfirm())
      .catch(() => {
        console.info('Leave break canceled');
      });
  }

  private startBreak() {
    if (!this.agentId) {
      console.error('Agent ID is required for status change');
      return;
    }

    this.isLoading = true;

    this.loadAvailableBreakTypes()
      .then((breakTypes: BreakType[]) => {
        this.isLoading = false;
        this.cdr.detectChanges();

        if (_.size(breakTypes) > 1) {
          return this.agentDialogService.selectBreakType(null, breakTypes);
        }
        return _.get(_.head(breakTypes), 'id');
      })
      .then((breakTypeId: string) => {
        this.updateStatus(AgentStatusType.OnDutyOnBreak, undefined, breakTypeId);
      })
      .catch(() => {
        console.info('Break type selection canceled');
        this.isLoading = false;
        this.cdr.detectChanges();
      });
  }

  private loadAvailableBreakTypes(): Promise<BreakType[]> {
    const profile = this.agentConfigurationProfile
      || this.agentConfigurationProfilesRequestsService.getConfigurationByAgent(this.agentId);

    return Promise.resolve(profile)
      .then((agentProfile) => this.agentConfigurationProfilesRequestsService.getBreakTypesForProfile(agentProfile.id))
      .catch((err) => {
        console.error('Failed to load agent break types:', err);
        return this.splitService.getBreakTypes();
      })
      .then((breakTypes: BreakType[]) => _.reject(breakTypes, 'archive'));
  }

  private updateStatus(status: AgentStatusType, resource?: any, breakTypeId?: string) {
    if (!this.agentId) {
      console.error('Agent ID is required for status change');
      return;
    }

    this.isLoading = true;

    if (this.isTimekeepingEnabled) {
      // Use attendance APIs when timekeeping module is enabled
      let apiCall;

      if (status === AgentStatusType.OnDuty) {
        if (!this.agentConfigurationProfile) {
          console.error('Agent configuration profile is required');
          this.isLoading = false;
          return;
        }

        const provider = _.get(this.agentConfigurationProfile, 'shiftRestrictions.agentClockInProviderPreferences.provider');
        const enforceResourceBasedLogin = this.isEnforceResourceBasedLogin();

        // Clock in with resource if provided
        if (resource && resource.id && resource.provider) {
          apiCall = this.attendanceRequestsService.clockIn(this.agentId, {
            id: resource.id,
            providerType: resource.provider
          });
        } 
        // Clock in without resource if allowed (Default provider OR enforceResourceBasedLogin=false)
        else if (provider === ClockInResourceProviderType.Default || !enforceResourceBasedLogin) {
          apiCall = this.attendanceRequestsService.clockIn(this.agentId);
        } 
        // Resource required but not provided
        else {
          console.error('Resource is required for clock-in with this provider configuration');
          this.isLoading = false;
          return;
        }
      } else if (status === AgentStatusType.OffDuty) {
        apiCall = this.attendanceRequestsService.clockOut(this.agentId);
      } else if (status === AgentStatusType.OnDutyOnBreak) {
        apiCall = this.attendanceRequestsService.startBreak(this.agentId, breakTypeId);
      }

      if (!apiCall) {
        console.error('Invalid status change request');
        this.isLoading = false;
        return;
      }

      apiCall
        .then((res) => {
          this.agentsAlerts.updateStatusSuccess(status);
          this.analyticsService.trackEvent('Agents Actions', 'Change Status', status);
          this.onChangeStatus.emit();
        })
        .catch((error) => {
          this.agentsAlerts.updateStatusError(error);
          console.error('Failed to change agent status via attendance API:', error);
          this.analyticsService.trackEvent('Agents Actions', 'Change Status', 'Error');
        })
        .finally(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        });
    } else {
      // Use legacy API when timekeeping module is disabled
      this.agentsService.changeAgentStatus(status, this.agentId)
        .then((res) => {
          this.analyticsService.trackEvent('Agents Actions', 'Change Status', status);
          this.onChangeStatus.emit();
        })
        .catch((error) => {
          this.agentsAlerts.updateStatusError();
          console.error('Failed to change agent status:', error);
          this.analyticsService.trackEvent('Agents Actions', 'Change Status', 'Error');
        })
        .finally(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        });
    }
  }
}
